import React, { useEffect, useState } from "react";
import { MoreHorizontal, Users } from "lucide-react";
import { toast } from "sonner";

import { useAgentUsers, useSetAgentUserStatus, type PeopleFilter } from "@/shared/api/onboarding";
import { GhostButton, SearchInput } from "@/shared/components/ui-kit";
import { Segmented } from "@/shared/components/teams-ui";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { ViaIcon } from "@/shared/components/ViaIcon";
import { ago } from "@/shared/lib/time";
import { questionsOf, said, verifiedPhone } from "../blocks";
import { glyphOf } from "../knownOn";
import { PersonConfirm, type PersonAction } from "./PersonConfirm";
import { StatusPill } from "./StatusPill";

import type { AgentUser, AgentUserStatus, OnboardingFlow } from "@/shared/types";

const SEARCH_WAIT_MS = 250;
const FILTERS: { value: AgentUserStatus | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "onboarding", label: "Onboarding" },
  { value: "active", label: "Active" },
  { value: "blocked", label: "Blocked" },
];
const cell = "py-2.5 pr-4";
const menuItem = "text-[13px]";

/* Everyone who has come to the agent: what they told onboarding (a column per question of
   the flow), the email they signed in with, the phone they verified when the flow asks for
   one, the policy they land on, where they stand. Search and the status filter ask the
   server; a row opens the person, its menu blocks, restarts or deletes them. */
export function PeopleTable({ agentId, flow, onOpen }: {
  agentId: string;
  flow: OnboardingFlow | undefined;
  onOpen: (person: AgentUser) => void;
}) {
  const [typed, setTyped] = useState("");
  const [filter, setFilter] = useState<PeopleFilter>({ status: null, q: "" });
  const [confirm, setConfirm] = useState<{ person: AgentUser; action: PersonAction } | null>(null);
  const list = useAgentUsers(agentId, filter);
  const setStatus = useSetAgentUserStatus();

  useEffect(() => {
    const wait = setTimeout(() => setFilter((f) => (f.q === typed ? f : { ...f, q: typed })), SEARCH_WAIT_MS);
    return () => clearTimeout(wait);
  }, [typed]);

  const fields = questionsOf(flow?.blocks ?? []);
  const asksPhone = !!flow?.blocks.some((b) => b.type === "phone");
  const people = list.data?.pages.flat() ?? [];
  const filtered = !!filter.status || !!filter.q;
  // everyone on the client app or in the extension has their account's email; a channel's don't
  const showEmail = people.some((p) => p.email);

  const block = (person: AgentUser, status: "active" | "blocked") =>
    setStatus.mutate(
      { id: person.id, status },
      { onSuccess: () => toast.success(status === "blocked" ? `${person.name ?? "They"} won't be answered` : "Unblocked") },
    );

  return (
    <section aria-label="People" className="min-w-0">
      <div className="pb-3 flex flex-wrap items-center gap-2">
        <SearchInput
          raised
          aria-label="Search people"
          placeholder="Name, answer or email"
          value={typed}
          maxLength={100}
          onChange={(e) => setTyped(e.target.value)}
          className="w-full sm:w-[260px]"
        />
        <div className="flex-1" />
        <Segmented
          role="radiogroup"
          label="Status"
          value={filter.status ?? "all"}
          onChange={(v) => setFilter((f) => ({ ...f, status: v === "all" ? null : v }))}
          options={FILTERS}
        />
      </div>

      <div>
        {list.isPending ? (
          <div className="py-10 text-center text-[12.5px] text-[var(--text-tertiary)]">Loading…</div>
        ) : people.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 fade-in">
            <div className="w-10 h-10 rounded-[12px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] flex items-center justify-center">
              <Users className="w-[18px] h-[18px] text-[var(--text-tertiary)]" strokeWidth={1.5} />
            </div>
            <div className="text-[15px] font-medium tracking-[-0.005em]">{filtered ? "Nobody matches" : "Nobody yet"}</div>
            <div className="text-[13px] text-[var(--text-tertiary)] max-w-[340px] text-center">
              {filtered
                ? "Try another name, or another status."
                : "People appear here when they first write to the agent on WhatsApp, Telegram, Slack, Discord or Teams."}
            </div>
          </div>
        ) : (
          <>
            {/* one line per row; on a phone the table scrolls sideways */}
            <div className="overflow-x-auto">
              <table className="w-full text-[13px] whitespace-nowrap">
                <thead>
                  <tr className="text-left">
                    {["Name", ...(showEmail ? ["Email"] : []), ...(asksPhone ? ["Phone"] : []), ...fields.map((f) => f.label),
                      "Policy", "Channels", "Status", "Last message", ""].map((h, i) => (
                      <th key={`${h}-${i}`} className="section-label font-medium py-2 pr-4">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {people.map((person) => (
                    <tr
                      key={person.id}
                      onClick={() => onOpen(person)}
                      className="border-t hairline cursor-pointer hover:bg-[var(--accent-hover)] transition-colors"
                    >
                      <td className={`${cell} font-medium text-[var(--text-primary)] max-w-[200px] truncate`} title={person.name ?? undefined}>
                        {person.name ?? <span className="text-[var(--text-quaternary)]">—</span>}
                      </td>
                      {showEmail && <Proved value={person.email} />}
                      {asksPhone && <Proved value={verifiedPhone(flow?.blocks ?? [], person.proved) ?? null} number />}
                      {fields.map((f) => (
                        <td key={f.key ?? f.label} className={`${cell} text-[var(--text-secondary)] max-w-[220px] truncate`}>
                          {said(person.answers[f.key ?? ""]) ?? <span className="text-[var(--text-quaternary)]">—</span>}
                        </td>
                      ))}
                      <PolicyCell rule={person.rule} />
                      <td className={cell}>
                        <span className="inline-flex items-center gap-1.5">
                          {person.channels.map((type) => <ViaIcon key={type} type={glyphOf(type)} />)}
                        </span>
                      </td>
                      <td className={cell}><StatusPill person={person} /></td>
                      <td className={`${cell} font-mono tabular-nums text-[12px] text-[var(--text-tertiary)]`}>
                        {person.last_message_at ? ago(person.last_message_at) : "—"}
                      </td>
                      <td className="py-1.5 text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              aria-label={`${person.name ?? "Person"} options`}
                              className="press w-7 h-7 rounded-full inline-flex items-center justify-center text-[var(--text-secondary)] hover:bg-[var(--accent-hover)]"
                            >
                              <MoreHorizontal className="w-[14px] h-[14px]" strokeWidth={1.75} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {person.status === "blocked" ? (
                              <DropdownMenuItem className={menuItem} onSelect={() => block(person, "active")}>Unblock</DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem className={menuItem} onSelect={() => block(person, "blocked")}>Block</DropdownMenuItem>
                            )}
                            <DropdownMenuItem className={menuItem} onSelect={() => setConfirm({ person, action: "restart" })}>
                              Restart onboarding
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className={`${menuItem} text-[var(--danger)] focus:text-[var(--danger)]`}
                              onSelect={() => setConfirm({ person, action: "delete" })}
                            >
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {list.hasNextPage && (
              <div className="pt-3 flex justify-center">
                <GhostButton
                  className="disabled:opacity-40 disabled:pointer-events-none"
                  onClick={() => list.fetchNextPage()}
                  disabled={list.isFetchingNextPage}
                >
                  {list.isFetchingNextPage ? "Loading…" : "Load more"}
                </GhostButton>
              </div>
            )}
          </>
        )}
      </div>

      {confirm && <PersonConfirm person={confirm.person} action={confirm.action} onClose={() => setConfirm(null)} />}
    </section>
  );
}

/** The policy they land on now, red when it keeps them out; none is Everyone else. */
function PolicyCell({ rule }: { rule: AgentUser["rule"] }) {
  return (
    <td
      className={`${cell} max-w-[180px] truncate ${rule?.action === "block" ? "text-[var(--danger)]" : "text-[var(--text-secondary)]"}`}
      title={rule?.name}
    >
      {rule?.name ?? "Everyone else"}
    </td>
  );
}

/** What they proved — the email they signed in with, the phone they verified — with its
    tick; a dash when not (yet). */
function Proved({ value, number = false }: { value: string | null; number?: boolean }) {
  if (!value) return <td className={`${cell} text-[var(--text-quaternary)]`}>—</td>;
  return (
    <td className={`${cell} text-[var(--text-secondary)]`}>
      <span className="inline-flex items-center gap-1.5">
        <span aria-label="verified" className="text-[var(--success)]">✓</span>
        {number ? <span className="font-mono tabular-nums text-[12px]">{value}</span> : value}
      </span>
    </td>
  );
}
